// src/pages/misc/landing/LandingPathStep.tsx
// First setup question, shown as the last section of the landing.
// Continue saves the choice and opens the next question at /onboarding.
//
// [HANDOFF] LANDING -> ONBOARDING BRIDGE (beat 9, #cuestionario). This is the builder's first screen (S1 "¿Cuál eres?") embedded in the landing.
//   What it writes:
//     localStorage   ventaje_draft_v3   only the `path` field (0 none, 1 "Formo parte", 2 "Mi empresa", 3 "Varias empresas"). The rest of the draft
//                                       is created later by structureDraft.ts, which keeps any existing fields.
//     sessionStorage ventaje_s1_done    "1" when Continuar is pressed. The builder (StructureBuilderPage) then SKIPS its own first screen
//                                       and starts at the next question, as long as path is not 0.
//   Then it calls onContinue(), which the landing wires to navigate('/onboarding').
//   Default when nothing is saved (or the saved path is 0): readPath() returns 2, so "Mi empresa" is pre-selected and Continuar is visible at once.
//   Nothing is written to localStorage until the user touches the slider / pictures or presses Continuar.
// [VERIFY] POS, MINIS, CHOICE and the slider JSX are COPIES of the ones in onboarding/StructureBuilderPage.tsx. If you change one, change both.
// [SPACING] Uses the builder's CSS (../onboarding/builder.css, wrapped in `.ob-embed`): see builder.css for slider, picture and foot sizes.
//   The Continuar button is hidden only when path is 0 (never the case here, see the default above), same rule as the builder.
// [I18N] Texts are typed Spanish.
import React, { useState } from 'react';
import '../onboarding/builder.css';

export type SetupPath = 0 | 1 | 2 | 3;

const DRAFT_KEY = 'ventaje_draft_v3';
export const SETUP_STEP_DONE = 'ventaje_s1_done';
const POS = [2, 12, 50, 88];

const MINIS = [
  '<svg viewBox="0 0 90 80" fill="none" stroke="#A9E0F4" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="45" cy="20" r="11"/><circle cx="45" cy="17" r="3.4"/><path d="M39.5 25c0-3 2.5-5 5.5-5s5.5 2 5.5 5"/><path d="M45 31v12"/><rect x="31" y="43" width="28" height="22" rx="4"/><path d="M38 65V52h14v13M42 50h1M48 50h1"/></svg>',
  '<svg viewBox="0 0 90 80" fill="none" stroke="#A9E0F4" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="32" y="8" width="26" height="22" rx="4"/><path d="M39 30V17h12v13M43 15h1M48 15h1"/><path d="M45 30v10M45 40H22v8M45 40h23v8M45 40v8"/><rect x="14" y="48" width="16" height="16" rx="4"/><rect x="37" y="48" width="16" height="16" rx="4"/><rect x="60" y="48" width="16" height="16" rx="4"/><circle cx="22" cy="56" r="2.2"/><circle cx="45" cy="56" r="2.2"/><circle cx="68" cy="56" r="2.2"/></svg>',
  '<svg viewBox="0 0 90 80" fill="none" stroke="#A9E0F4" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="36" y="4" width="18" height="15" rx="4"/><circle cx="45" cy="11.5" r="2.4"/><path d="M45 19v7M45 26H20v6M45 26h25v6M45 26v6"/><rect x="12" y="32" width="16" height="13" rx="4"/><rect x="37" y="32" width="16" height="13" rx="4"/><rect x="62" y="32" width="16" height="13" rx="4"/><path d="M20 45v6M45 45v6M70 45v6M14 51h12M39 51h12M64 51h12M14 51v5M26 51v5M39 51v5M51 51v5M64 51v5M76 51v5"/><g stroke-width="1.3"><rect x="9" y="56" width="10" height="9" rx="3"/><rect x="21" y="56" width="10" height="9" rx="3"/><rect x="34" y="56" width="10" height="9" rx="3"/><rect x="46" y="56" width="10" height="9" rx="3"/><rect x="59" y="56" width="10" height="9" rx="3"/><rect x="71" y="56" width="10" height="9" rx="3"/></g></svg>',
];

const CHOICE = [
  '',
  '<b>Formo parte de una empresa.</b><em>Vendo en el equipo de una <span class="ob-key">inmobiliaria</span>, un <span class="ob-key">broker</span> o una <span class="ob-key">desarrolladora</span>.</em>',
  '<b>Tengo mi empresa.</b><em>Soy <span class="ob-key">desarrollador</span> o <span class="ob-key">constructora</span>: manejo varios proyectos y mi propia empresa los vende.</em>',
  '<b>Administro varias empresas.</b><em>Soy <span class="ob-key">broker</span>, <span class="ob-key">master broker</span> o <span class="ob-key">inmobiliaria</span>, y vendo o administro la cuenta de varias desarrolladoras.</em>',
];

function readPath(): SetupPath {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return 2;
    const n = Number(JSON.parse(raw).path);
    return n === 1 || n === 2 || n === 3 ? n : 2;
  } catch {
    return 2;
  }
}

function writePath(path: SetupPath) {
  let draft: Record<string, unknown> = {};
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (raw) draft = JSON.parse(raw);
  } catch { /* start fresh */ }
  draft.path = path;
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify(draft)); } catch { /* ignore */ }
}

const LandingPathStep: React.FC<{ onContinue: () => void }> = ({ onContinue }) => {
  const [path, setPath] = useState<SetupPath>(readPath);

  const choose = (next: SetupPath) => {
    setPath(next);
    writePath(next);
  };

  const go = () => {
    if (path === 0) return;
    writePath(path);
    try { sessionStorage.setItem(SETUP_STEP_DONE, '1'); } catch { /* ignore */ }
    onContinue();
  };

  return (
    <div className="ob-embed">
      <div className="ob-s1">
        <div className="ob-minis">
          {MINIS.map((svg, i) => (
            <button
              key={i}
              type="button"
              className={`ob-mini${path === i + 1 ? ' on' : ''}`}
              aria-pressed={path === i + 1}
              aria-label={['Formo parte', 'Mi empresa', 'Varias empresas'][i]}
              onClick={() => choose((i + 1) as SetupPath)}
              dangerouslySetInnerHTML={{ __html: svg }}
            />
          ))}
        </div>
        <div className="ob-choice" dangerouslySetInnerHTML={{ __html: CHOICE[path] }} />
      </div>
      <div className="ob-panel">
        <div className="ob-card">
          <h1 className="ob-q">¿Cuál <b>eres?</b></h1>
          <p className="ob-hint">Desliza para ver →</p>
          <div className="ob-ctrl">
            <div className="ob-slider">
              <div className="ob-track">
                <div className="ob-sfill" style={{ width: `${POS[path]}%` }} />
                {[12, 50, 88].map((left, i) => (
                  <span key={left} className={`ob-stop${path >= i + 1 ? ' on' : ''}`} style={{ left: `${left}%` }} />
                ))}
                <div className="ob-knob" style={{ left: `${POS[path]}%` }} />
                <input
                  className="ob-srange"
                  type="range"
                  min={0}
                  max={3}
                  step={1}
                  value={path}
                  aria-label="Qué tipo de operación tienes"
                  onChange={(e) => choose(Number(e.target.value) as SetupPath)}
                />
              </div>
              <div className="ob-slabels"><span>Formo parte</span><span>Mi empresa</span><span>Varias empresas</span></div>
            </div>
          </div>
        </div>
        <div className="ob-foot">
          <span />
          <div className="ob-foot-right">
            {path === 0 ? null : (
              <button
                type="button"
                className="btn-gold inline-flex items-center justify-center min-h-[52px] px-7 rounded-[10px] text-[12px] font-bold uppercase tracking-[0.1em]"
                onClick={go}
              >
                Continuar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPathStep;
